import {
  AccountsByApplication,
  AccountsWithNameByApplication,
  Application,
} from "../infrastructure/api/api";

export const parseScopes = (flatScopes: string[]): AccountsByApplication => {
  const grouped: AccountsByApplication = {};

  for (const scope of flatScopes) {
    const [oAuthId, account] = scope.split(":");
    if (oAuthId && account) {
      grouped[oAuthId] = [...(grouped[oAuthId] ?? []), account];
    }
  }

  return grouped;
};

export const toggleScope = (
  scopes: AccountsByApplication,
  oAuthId: string,
  account: string,
  checked: boolean,
): AccountsByApplication => {
  const accounts = scopes[oAuthId] ?? [];
  return {
    ...scopes,
    [oAuthId]: checked
      ? accounts.includes(account)
        ? accounts
        : [...accounts, account]
      : accounts.filter((selected) => selected !== account),
  };
};

export const hasAnyScope = (scopes: AccountsByApplication): boolean =>
  Object.values(scopes).some((accounts) => accounts.length > 0);

export const applicationDisplayName = (
  oAuthId: string,
  applications: Application[],
): string =>
  applications.find((application) => application.oAuthId === oAuthId)
    ?.displayName ?? oAuthId;

export interface ScopeRow {
  accountId: string;
  name?: string;
  granted: boolean;
}

export interface ApplicationScopes {
  oAuthId: string;
  displayName: string;
  accounts: ScopeRow[];
}

export const scopeOverview = (
  applications: Application[],
  availableAccounts: AccountsWithNameByApplication,
  grantedScopes: AccountsByApplication,
): ApplicationScopes[] => {
  const oAuthIds = [
    ...new Set([
      ...Object.keys(availableAccounts),
      ...Object.keys(grantedScopes),
    ]),
  ];

  return oAuthIds
    .map((oAuthId) => {
      const available = availableAccounts[oAuthId] ?? [];
      const granted = grantedScopes[oAuthId] ?? [];

      const accounts: ScopeRow[] = available.map((account) => ({
        accountId: account.accountId,
        name: account.name,
        granted: granted.includes(account.accountId),
      }));

      for (const accountId of granted) {
        if (!available.some((account) => account.accountId === accountId)) {
          accounts.push({ accountId, granted: true });
        }
      }

      return {
        oAuthId,
        displayName: applicationDisplayName(oAuthId, applications),
        accounts,
      };
    })
    .filter(({ accounts }) => accounts.length > 0);
};

export interface ScopeOption {
  scope: string;
  resource: string | undefined;
  label: string;
}

export const scopeOptions = (
  scopes: string[],
  applications: Application[],
  availableAccounts: AccountsWithNameByApplication,
): ScopeOption[] =>
  scopes.map((scope) => {
    const [apiName = "", accountId = ""] = scope.split(":");

    const accountName = availableAccounts[apiName]?.find(
      (account) => account.accountId === accountId,
    )?.name;

    return {
      scope,
      resource: applications.find(
        (application) => application.oAuthId === apiName,
      )?.oAuthResourceIdentifier,
      label: `${applicationDisplayName(apiName, applications)} – ${accountName ?? accountId}`,
    };
  });
